import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  IMeasureFieldsFragment,
  INamedMeasureEditMutation,
  INamedMeasureEditMutationVariables,
  INamedMeasuresQueryQuery,
  INamedMeasuresQueryQueryVariables,
  IVersionFieldsFragment,
  PermissionKey,
  SearchNamedMeasureOption,
} from '../../../utils/apollo/graphqlSchema';
import {
  EDIT_NAMED_MEASURE,
  GET_NAMED_MEASURES,
  NAMED_MEASURE_ADD_CATEGORY,
  NAMED_MEASURE_REMOVE_CATEGORY,
} from '../../../utils/apollo/measures';
import { useDebounce } from '../../../utils/hooks';
import { useAppSelector } from '../../../redux/hooks';
import { assertNotNil } from '../../../utils/types';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import MeasuresTable from './MeasuresTable';
import useApplyTags from '../useApplyTags';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { CategoryEntityType } from '../../../components/Categories/utils';
import { ManagedEntityType } from '../../../utils/apollo/graphqlSchema';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { ModalSliceType } from '../../../redux/slices/modal';
import DeleteMeasureModal from './DeleteMeasureModal';
import { useDictionarySearchParamsEffect } from '../../../utils/searchParamsDataSharing';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import DictionaryNoResults from '../DictionaryNoResults';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { RowSelectionState } from '@tanstack/react-table';
import DictionaryHeader from '../utils/DictionaryHeader';
import { useGlobalApplyTags } from '../utils/useGlobalApplyTags';
const fallbackData: IMeasureFieldsFragment[] = [];

const Measures = () => {
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'dictionary',
        name: 'measure',
        id: null,
      }),
    );
  }, [track]);

  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.MeasureRead,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // populate schemaId + search term from search params data sharing
  useDictionarySearchParamsEffect(setSearchQuery);

  const { checkIsPermitted } = usePermission();
  const isPermittedToAssignCategory = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.CategoryCreate,
      PermissionKey.MeasureEdit,
    ],
    schemaId: selectedSchemaId,
  });

  const isDeleteMeasureModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DeleteMeasure, ''),
  );

  const { data, loading, refetch } = useQuery<
    INamedMeasuresQueryQuery,
    INamedMeasuresQueryQueryVariables
  >(GET_NAMED_MEASURES, {
    variables: {
      term: debouncedSearchQuery,
      schemaId: selectedSchemaId,
      includeIds: [],
      searchNamedMeasureOption: SearchNamedMeasureOption.All,
      pagination: {
        first: 0,
        after: 0,
      },
    },
  });

  const namedMeasures = data?.namedMeasures?.entities ?? fallbackData;
  const handleSubmitNewTags = () => {
    refetch();
    setRowSelection({});
  };

  const getEntityId = (entity: IMeasureFieldsFragment) => {
    assertNotNil(entity.version);
    return entity.version.versionId;
  };

  const handleApplyTags = useApplyTags({
    entityType: CategoryEntityType.Measure,
    managedEntityType: ManagedEntityType.Measure,
    addCategoryMutation: NAMED_MEASURE_ADD_CATEGORY,
    removeCategoryMutation: NAMED_MEASURE_REMOVE_CATEGORY,
    getEntityId,
    getEntityCategories: (entity: IMeasureFieldsFragment) => entity.categories,
    onSuccess: handleSubmitNewTags,
  });

  const handleEditMutationComplete = ({
    namedMeasureEdit,
  }: INamedMeasureEditMutation) => {
    const { entity, errors } = namedMeasureEdit;

    if (printGraphqlErrors(errors)) {
      return;
    }

    assertNotNil(entity);

    toastMessage({
      message: `${entity.name} has been updated.`,
      type: 'info',
    });

    refetch();
  };
  const [editNamedMeasure] = useMutation<
    INamedMeasureEditMutation,
    INamedMeasureEditMutationVariables
  >(EDIT_NAMED_MEASURE, {
    onCompleted: handleEditMutationComplete,
  });

  const handleChangeMeasureName = (
    version: IVersionFieldsFragment,
    newName: string,
  ) => {
    editNamedMeasure({
      variables: {
        input: {
          name: newName.trim(),
          measure: null,
          comment: null,
          description: null,
          namedMeasureId: version.versionId,
          categories: null,
        },
      },
    });
  };

  const handleChangeMeasureDescription = (
    version: IVersionFieldsFragment,
    newDescription: string,
  ) => {
    editNamedMeasure({
      variables: {
        input: {
          name: null,
          measure: null,
          comment: null,
          description: newDescription.trim(),
          namedMeasureId: version.versionId,
          categories: null,
        },
      },
    });
  };

  const handleGlobalApplyTags = useGlobalApplyTags({
    rowSelection,
    entities: namedMeasures,
    selectedSchemaId,
    handleApplyTags,
    getEntityId,
  });

  const selectedEntityCategories = namedMeasures
    .filter((measure) => rowSelection[getEntityId(measure)])
    .map((measure) => ({ categories: measure.categories }));

  return (
    <>
      <DictionaryHeader
        selectedSchemaId={selectedSchemaId}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        title="Measure"
        dictionaryItemCount={namedMeasures.length}
        permittedSchemaIds={permittedSchemaIds}
        onApplyTags={handleGlobalApplyTags}
        isPermittedToAssignCategory={isPermittedToAssignCategory}
        showApplyTags={Object.keys(rowSelection).length > 0}
        selectedEntities={selectedEntityCategories}
      />
      {loading ? (
        <ContentLoader />
      ) : namedMeasures.length === 0 ? (
        <DictionaryNoResults
          entityType={DictionaryTypeEnum.DictionaryMeasure}
        />
      ) : (
        <MeasuresTable
          namedMeasures={namedMeasures}
          schemaId={selectedSchemaId}
          onApplyTags={handleApplyTags}
          onChangeName={handleChangeMeasureName}
          onChangeDescription={handleChangeMeasureDescription}
          onSetFavorite={refetch}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          isPermittedToAssignCategory={isPermittedToAssignCategory}
        />
      )}
      {isDeleteMeasureModalOpen && <DeleteMeasureModal onSubmit={refetch} />}
    </>
  );
};

export default Measures;
